import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { BRAND } from "../data/site";

const LINKS = [
  { to: "/services", label: "Services" },
  { to: "/pricing", label: "Pricing & Plans" },
  { to: "/booking", label: "Book Now" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/90 backdrop-blur-xl shadow-lg shadow-brand-emerald/5 border-b border-[#D6E4DC]" : "bg-white/60 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
            <img src="/assets/logo.png" alt="Neat & Proper logo" className="h-10 w-10 lg:h-12 lg:w-12 rounded-xl object-cover shadow-md shadow-brand-emerald/20 group-hover:scale-105 transition-transform" />
            <div className="leading-tight">
              <span className="font-display font-800 font-extrabold text-brand-emerald text-lg lg:text-xl block">Neat &amp; Proper</span>
              <span className="text-[10px] lg:text-xs text-brand-muted tracking-wide hidden sm:block">{BRAND.descriptor}</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" data-testid="nav-links">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                    isActive ? "bg-brand-mint text-brand-emerald" : "text-brand-muted hover:text-brand-emerald hover:bg-brand-mint/60"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a href={BRAND.phoneHref} data-testid="nav-phone" className="flex items-center gap-2 text-sm font-semibold text-brand-emerald hover:text-brand-lime transition-colors">
              <Phone className="w-4 h-4" /> {BRAND.phone}
            </a>
            <Link
              to="/booking"
              data-testid="nav-book-now-btn"
              className="px-5 py-2.5 rounded-full bg-brand-emerald text-white text-sm font-bold hover:bg-brand-lime hover:shadow-lg hover:shadow-brand-lime/30 transition-all duration-300"
            >
              Book a Clean
            </Link>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 rounded-lg text-brand-emerald hover:bg-brand-mint"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-[#D6E4DC] shadow-xl animate-pop-in" data-testid="nav-mobile-menu">
          <div className="px-4 py-4 space-y-1">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-xl font-semibold ${isActive ? "bg-brand-emerald text-white" : "text-brand-emerald hover:bg-brand-mint"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a href={BRAND.phoneHref} className="block px-4 py-3 text-brand-muted font-semibold">{BRAND.phone}</a>
          </div>
        </div>
      )}
    </header>
  );
}
